/**
 * Labels for member + provider fields shown together on enrollment / provider UIs.
 */

export function formatGenderDisplay(g) {
  if (g == null || g === '') return '';
  const s = String(g).trim();
  if (!s) return '';
  const u = s.toUpperCase();
  if (u === 'M' || u === 'MALE') return 'Male';
  if (u === 'F' || u === 'FEMALE') return 'Female';
  if (u === 'U' || u === 'UNKNOWN') return 'Unknown';
  return s;
}

function isTruthyCert(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'boolean') return v;
  const s = String(v).trim().toLowerCase();
  if (['yes', 'y', 'true', '1', 'on'].includes(s)) return true;
  if (['no', 'n', 'false', '0', 'off'].includes(s)) return false;
  return null;
}

/**
 * @param {Record<string, unknown>|null|undefined} provider
 * @returns {'Yes'|'No'|null}
 */
export function boardCertifiedDisplay(provider) {
  if (!provider || typeof provider !== 'object') return null;
  const keys = [
    'BOARD_CERTIFIED',
    'BoardCertified',
    'boardCertified',
    'board_certified',
    'BOARD_CERT',
    'IsBoardCertified',
    'isBoardCertified',
  ];
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (Object.prototype.hasOwnProperty.call(provider, k) && provider[k] !== undefined && provider[k] !== '') {
      const t = isTruthyCert(provider[k]);
      if (t === null) return String(provider[k]);
      return t ? 'Yes' : 'No';
    }
  }
  return null;
}

export function formatDobShort(d) {
  if (d == null || d === '') return '';
  try {
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return String(d);
    return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return String(d);
  }
}

/**
 * Rows for "Member information" (member identity only — not provider gender).
 * @param {Record<string, unknown>|null|undefined} member
 * @returns {{ key: string, label: string, value: string }[]}
 */
export function buildMemberInfoRows(member) {
  const m = member;
  if (!m || typeof m !== 'object') return [];
  const rows = [];
  const name =
    m.name ||
    m.MEMBER_NAME ||
    [m.firstName, m.first_name, m.FIRST_NAME, m.lastName, m.last_name, m.LAST_NAME].filter(Boolean).join(' ').trim() ||
    '';
  if (name) rows.push({ key: 'name', label: 'Name', value: name });
  const mid = m.member_id || m.memberId || m.MEMBER_ID || m.memberID;
  if (mid != null && String(mid).trim() !== '') rows.push({ key: 'member_id', label: 'Member ID', value: String(mid) });
  const dobRaw = m.date_of_birth || m.dob || m.DATE_OF_BIRTH || m.dateOfBirth;
  if (dobRaw) {
    const dobShort = formatDobShort(dobRaw);
    if (dobShort) rows.push({ key: 'dob', label: 'Date of birth', value: dobShort });
  }
  return rows;
}

const PROVIDER_GENDER_KEYS = [
  'GENDER',
  'Gender',
  'gender',
  'SEX',
  'Sex',
  'sex',
  'PROVIDER_GENDER',
  'ProviderGender',
  'providerGender',
  'provider_gender',
  'RENDERING_PROVIDER_GENDER',
  'RenderingProviderGender',
  'rendering_provider_gender',
];

/**
 * Gender for display on provider detail UIs (not the member/patient).
 * @param {Record<string, unknown>|null|undefined} provider
 * @returns {string} Label like "Male" / "Female", or "—" if unknown
 */
export function providerGenderLine(provider) {
  if (!provider || typeof provider !== 'object') return '—';
  for (let i = 0; i < PROVIDER_GENDER_KEYS.length; i++) {
    const k = PROVIDER_GENDER_KEYS[i];
    if (!Object.prototype.hasOwnProperty.call(provider, k)) continue;
    const raw = provider[k];
    if (raw === undefined || raw === null || raw === '') continue;
    const line = formatGenderDisplay(raw);
    if (line) return line;
  }
  return '—';
}
